import { useMemo } from "react";
import { useReadContracts } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { ELECTION_ABI } from "@/contract/abi/election";
import ContractAddress from "@/contract/addresses";
import { $api } from "@/lib/api-client";
import type { components } from "@/types/api/api-types";
import type { MergedCandidate } from "@/types/dao/election";

const OWNER_SLOT_COUNT = 21;
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

type UserByAddress = components["schemas"]["UserByAddressResponseDto"] & {
  email?: string | null;
  name?: string | null;
};

function isOwnerAddress(value: unknown): value is `0x${string}` {
  return (
    typeof value === "string" &&
    value.startsWith("0x") &&
    value.length === 42 &&
    value.toLowerCase() !== ZERO_ADDRESS
  );
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function buildOwnerReads(cycle: number) {
  return Array.from({ length: OWNER_SLOT_COUNT }, (_, i) => ({
    address: ContractAddress.ELECTION,
    abi: ELECTION_ABI,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "ownersOfCycle" as const,
    args: [BigInt(cycle), BigInt(i)] satisfies [bigint, bigint],
  }));
}

export function useFetchDaoMembers(cycle: number | undefined) {
  const ownerReads = useReadContracts({
    contracts: cycle === undefined ? [] : buildOwnerReads(cycle),
    allowFailure: false,
    query: {
      enabled: cycle !== undefined,
      retry: false,
    },
  });

  const ownerAddresses = useMemo(() => {
    const raw = ownerReads.data ?? [];
    const addresses: `0x${string}`[] = [];

    for (const value of raw) {
      if (isOwnerAddress(value)) {
        addresses.push(value.toLowerCase() as `0x${string}`);
      }
    }

    return addresses;
  }, [ownerReads.data]);

  const lookupQuery = $api.useQuery(
    "get",
    "/user/by-addresses",
    {
      params: { query: { a: ownerAddresses } },
    },
    { enabled: ownerAddresses.length > 0 },
  );

  const identities: UserByAddress[] = lookupQuery.data?.data ?? [];

  const data = useMemo<MergedCandidate[] | undefined>(() => {
    if (ownerReads.data === undefined) return undefined;

    const identityByWallet = new Map<string, UserByAddress>();
    if (!lookupQuery.isError) {
      for (const identity of identities) {
        identityByWallet.set(identity.wallet_address.toLowerCase(), identity);
      }
    }

    return ownerAddresses.map((walletAddress) => {
      const identity = identityByWallet.get(walletAddress);

      return {
        walletAddress,
        name: nonEmptyString(identity?.name),
        nickname: nonEmptyString(identity?.nickname),
        email: nonEmptyString(identity?.email),
        totalReceivedVoteDnmWei: 0n,
        registrationId: null,
      };
    });
  }, [identities, lookupQuery.isError, ownerAddresses, ownerReads.data]);

  return {
    data,
    isLoading: ownerReads.isLoading || lookupQuery.isLoading,
    isError: ownerReads.isError,
    refetch: ownerReads.refetch,
  };
}
